/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * Module containing update related components.
 * @preferred
 * @module UpdateModule
 */

import './update-node-status-card.component.less';

import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    Type,
} from '@angular/core';

import { Subscription } from 'rxjs';

import {
    NodeType,
    UpgradeFsmState,
} from 'generated-types';

import { SchemaService } from 'ajs/modules/core/services/schema-service';

import {
    AviContinueConfirmationComponent,
} from 'ng/modules/dialog/components/avi-continue-confirmation/avi-continue-confirmation.component';

import { DialogService } from 'ng/modules/core/services/dialog.service';
import { DevLoggerService } from 'ng/modules/core/services/dev-logger.service';
import { ITUpgradeNode } from 'ajs/modules/upgrade';
import { UpdateService } from 'ng/modules/update/services/update.service';
import { ITSEGroup } from 'ajs/modules/service-engine-group/factories/se-group.item.factory';
import { Image } from 'ajs/modules/upgrade/factories/image.item.factory';
import { L10nService } from '@vmw/ngx-vip';

import {
    IUserSelectedOptions,
    UpgradeStateGroup,
} from '../../update.types';

import {
    UpgradeTranscriptModalComponent,
} from '../upgrade-transcript-modal/upgrade-transcript-modal.component';

import { UpdateStore } from '../update.store';

import * as l10n from './update-node-status-card.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const SUPRESS_WARNING_DIALOG_ID = 'SUPRESS_WARNING_DIALOG';
const UPGRADE_TRANSCRIPT_DIALOG_ID = 'UPGRADE_TRANSCRIPT_DIALOG_ID';

interface IStatusIconInfo {
    shape?: string;
    status?: string;
}

/**
 * @description Panel card for update pages.
 * @author Akul Aggarwal, Zhiqian Liu, Sarthak Kapoor
 */
@Component({
    selector: 'update-node-status-card',
    templateUrl: './update-node-status-card.component.html',
})
export class UpdateNodeStatusCardComponent implements OnInit, OnDestroy {
    @Input() public node: ITUpgradeNode;

    @Input()
    public overallPreChecksState?: UpgradeFsmState;

    @Input()
    public isPreCheckFlow = false;

    @Input()
    public userSelectedOptions: IUserSelectedOptions = {};

    @Input()
    public rollbackVersion: string;

    @Input()
    public isSystemUpdate = false;

    @Input()
    public selectedSegs: ITSEGroup[] = [];

    @Input()
    public selectedSegUpgradeImages: Image[] = [];

    @Input()
    public isUpgradeTriggeredFromSystemUpdatePage = false;

    @Input()
    public isPreCheckOnlyFlow = false;

    @Input()
    public isRestoreFlow = false;

    @Input()
    public isDryRunFlow = false;

    /**
     * Event emitted when user resumes the upgrade from the right rail card.
     */
    @Output()
    public onSelectedSegIdUpdate = new EventEmitter<string>();

    /**
     * Boolean determining if card's middle part is extended.
     */
    public showExpanded = true;

    public nodeName: string;

    public currentNodeVersion: string;

    public preChecksFailed = false;

    public preChecksCompletedWithWarning = false;

    public preChecksInProgress = false;

    /**
     * Value for Upgrade operation for node.
     */
    public nodeUpgradeOps: string;

    /**
     * Value for upgrade_ops for pre-checks.
     */
    public preChecksUpgradeOps = '';

    /**
     * String value time stamp when upgrade/rollback of node started.
     */
    public nodeUpgradeStartTime = '';

    /**
     * String value for time stamp when pre-checks were triggered.
     */
    public preChecksStartTime = '';

    /**
     * Contains information regarding which cds-icon to display, depending upon the node status.
     */
    public readonly statusIconInfo: IStatusIconInfo = {};

    /**
     * Description of node status from schema.
     */
    public nodeStatusDescription: string;

    /**
     * Node status to be displayed on ui depending upon the state of the node.
     */
    public nodeStateGroupLabel: string;

    /**
     * Flag to show/hide upgrade resume button.
     */
    public showUpgradeResumeButton = false;

    /**
     * Flag to keep track of pre-checks completion status.
     */
    public preChecksCompleted = false;

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Subscription to get updates on completion of pre-checks.
     */
    private onPreChecksCompleteSubscription: Subscription;

    /**
     * Flag to keep a track if pre checks completed successfully.
     * Required in Pre check only flow where we need to show the successful status.
     */
    private preChecksCompletedSuccessfully = false;

    public constructor(
        private readonly updateStore: UpdateStore,
        private readonly schemaService: SchemaService,
        private readonly dialogService: DialogService,
        private readonly devLoggerService: DevLoggerService,
        public readonly l10nService: L10nService,
    ) {
        this.l10nService = l10nService;

        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * @override
     * Update Node related info like pre-checks status, last upgrade status etc. on
     * 1.) Initial load
     * 2.) On successful load execution of upgradeNodeCollection
     * as we need to update these properties again once, the pre-checks are completed.
     */
    public ngOnInit(): void {
        const { updateStore } = this;

        this.setNodeRelatedInfo();
        this.onPreChecksCompleteSubscription = updateStore.preChecksCompleted$.subscribe(preChecksCompleted => {
            this.preChecksCompleted = preChecksCompleted;
            this.setShowUpgradeResumeButton();
        });
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const {
            node,
            overallPreChecksState,
            isPreCheckOnlyFlow,
            isRestoreFlow,
        } = changes;

        if (node?.currentValue && !node.firstChange) {
            this.setNodeRelatedInfo();
        }

        if (overallPreChecksState?.currentValue) {
            this.setShowUpgradeResumeButton();
        }

        if (isPreCheckOnlyFlow?.currentValue && !isPreCheckOnlyFlow.firstChange) {
            this.setStatusIconInfo();
        }

        if (isRestoreFlow?.currentValue && !isRestoreFlow.firstChange) {
            this.setStatusIconInfo();
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        this.onPreChecksCompleteSubscription?.unsubscribe();
    }

    /**
     * Shows/hides middle portion of card.
     */
    public toggleExpansion(): void {
        this.showExpanded = !this.showExpanded;
    }

    /**
     * Returns 'type' attr to pass to 'avi-icon' component.
     */
    public getAviIconType(): string {
        const { stateGroup } = this.node;

        switch (stateGroup) {
            case UpgradeStateGroup.GROUP_ERROR:
                return 'error';

            case UpgradeStateGroup.GROUP_COMPLETED:
                return 'success';

            default:
                return 'error';
        }
    }

    /**
     * Opens upgrade transcript modal.
     */
    public openTranscriptModal(): void {
        const {
            node: {
                id: nodeId,
                nodeType,
            },
            isDryRunFlow,
        } = this;

        // For system update page we need to show overall state of controller + SEGs.
        const selectedNodes: string[] =
            nodeType === NodeType.NODE_CONTROLLER_CLUSTER ? undefined : [nodeId];

        if (this.isPreCheckFlow) {
            this.updateStore.openPreCheckTranscript({
                readonly: !this.showUpgradeResumeButton || this.isPreCheckOnlyFlow,
                nodeIds: selectedNodes,
            });
        } else {
            this.dialogService.add({
                id: UPGRADE_TRANSCRIPT_DIALOG_ID,
                component: UpgradeTranscriptModalComponent as Type<Component>,
                componentProps: {
                    nodeId,
                    nodeType,
                    isDryRunFlow,
                    onClose: () => this.dialogService.remove(UPGRADE_TRANSCRIPT_DIALOG_ID),
                },
            });
        }
    }

    public openSuppressWarningDialog(): void {
        const {
            dialogService,
            l10nKeys,
            l10nService,
        } = this;

        dialogService.add({
            id: SUPRESS_WARNING_DIALOG_ID,
            component: AviContinueConfirmationComponent as Type<Component>,
            componentProps: {
                customHeader: l10nService.getMessage(l10nKeys.suppressWarningHeader),
                warning: l10nService.getMessage(l10nKeys.warningMessage),
                onConfirm: async() => {
                    await this.onSuppressWarningConfirmation();
                    dialogService.remove(SUPRESS_WARNING_DIALOG_ID);
                },
                onClose: () => {
                    dialogService.remove(SUPRESS_WARNING_DIALOG_ID);
                },
            },
        });
    }

    /**
     * Map node related info like name, version etc. to local component variables.
     */
    private setNodeRelatedInfo(): void {
        const {
            isSystemUpdate,
            node,
            overallPreChecksState,
        } = this;

        this.nodeName = node.getName();
        this.currentNodeVersion = this.getCurrentNodeVersion();
        this.nodeUpgradeOps = this.getNodeOperation();
        this.nodeUpgradeStartTime = this.getOperationStartTime();

        /**
         * In case of System Update, we need to set the state based on the overall state of
         * all the nodes in a single card. In case of SEG, the state has to be shown based
         * on state of individual nodes.
         */
        this.preChecksInProgress = isSystemUpdate ?
            overallPreChecksState === UpgradeFsmState.UPGRADE_PRE_CHECK_IN_PROGRESS :
            node.arePrechecksForNodeInProgress();

        this.preChecksFailed = isSystemUpdate ?
            overallPreChecksState === UpgradeFsmState.UPGRADE_PRE_CHECK_ERROR :
            node.preChecksFailed();

        this.preChecksCompletedWithWarning = isSystemUpdate ?
            overallPreChecksState === UpgradeFsmState.UPGRADE_PRE_CHECK_WARNING :
            node.preChecksCompletedWithWarning();

        this.preChecksCompletedSuccessfully =
            overallPreChecksState === UpgradeFsmState.UPGRADE_PRE_CHECK_SUCCESS;

        if (this.preChecksFailed || this.preChecksCompletedWithWarning ||
            this.preChecksInProgress ||
            this.isPreCheckOnlyFlow || this.isRestoreFlow && this.preChecksCompletedSuccessfully
        ) {
            this.setPreChecksRelatedInfo();
        }

        this.setStatusIconInfo();
        this.setNodeStatusDescription();
    }

    /**
     * Set pre-checks related info like start time, upgrade operation value etc.
     */
    private setPreChecksRelatedInfo(): void {
        const { node } = this;

        this.preChecksUpgradeOps = node.preChecksUpgradeOps;
        this.preChecksStartTime = node.preChecksStartTimeStamp;
    }

    /**
     * Depending upon the state upgrade/rollback or pre-checks, set the icon and and status
     * to be displayed.
     */
    private setStatusIconInfo(): void {
        const {
            isDryRunFlow,
            isPreCheckFlow,
            l10nService,
            l10nKeys,
            node,
            preChecksFailed,
            preChecksCompletedWithWarning,
            statusIconInfo,
        } = this;

        const stateGroup = isDryRunFlow && !isPreCheckFlow ? node.dryrunStateGroup :
            node.stateGroup;

        if (
            stateGroup === UpgradeStateGroup.GROUP_COMPLETED &&
            !preChecksFailed &&
            !preChecksCompletedWithWarning ||
            isDryRunFlow && preChecksCompletedWithWarning
        ) {
            statusIconInfo.shape = 'check-circle';
            statusIconInfo.status = 'success';

            if (this.isRestoreFlow) {
                this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.restoreSuccessfulLabel);
            } else if (this.isDryRunFlow) {
                this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.dryrunSuccessfulLabel);
            } else {
                this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.updateSuccessfulLabel);
            }

            return;
        }

        if (stateGroup === UpgradeStateGroup.GROUP_ERROR || preChecksFailed) {
            statusIconInfo.shape = 'exclamation-circle';
            statusIconInfo.status = 'danger';

            if (this.isPreCheckOnlyFlow) {
                this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.precheckFailedLabel);
            } else if (this.isRestoreFlow) {
                this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.restoreFailedLabel);
            } else if (this.isDryRunFlow) {
                this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.dryrunFailedLabel);
            } else {
                this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.updateFailedLabel);
            }

            return;
        }

        if (preChecksCompletedWithWarning) {
            statusIconInfo.shape = 'exclamation-triangle';
            statusIconInfo.status = 'warning';

            if (this.isPreCheckOnlyFlow) {
                this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.precheckCompletedLabel);
            } else if (this.isRestoreFlow) {
                this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.restoreStoppedLabel);
            } else {
                this.nodeStateGroupLabel = l10nService.getMessage(l10nKeys.updateStoppedLabel);
            }
        }
    }

    /**
     * Set the description from schema based on the state of node.
     */
    private setNodeStatusDescription(): void {
        const {
            isSystemUpdate,
            node,
            overallPreChecksState,
            schemaService,
        } = this;
        const nodeState = node.state ||
            isSystemUpdate ? overallPreChecksState : node.upgradeReadiness?.state?.state;

        if (nodeState) {
            this.nodeStatusDescription = schemaService.getEnumValue(
                'UpgradeFsmState',
                nodeState,
            ).description;
        }
    }

    /**
     * Show upgrade resume button on the right rail cards.
     * This will be shown only if before the completion of the pre checks
     * user is on the same page from where the checks were triggered.
     * It will be hidden in case of refresh as well.
     */
    private setShowUpgradeResumeButton(): void {
        const {
            isSystemUpdate,
            isUpgradeTriggeredFromSystemUpdatePage,
            overallPreChecksState,
            preChecksCompleted,
            preChecksCompletedWithWarning,
            userSelectedOptions,
        } = this;

        if (this.isDryRunFlow) {
            this.showUpgradeResumeButton = false;

            return;
        }

        if (Object.keys(userSelectedOptions).length && preChecksCompleted) {
            if (isSystemUpdate) {
                this.showUpgradeResumeButton =
                    overallPreChecksState === UpgradeFsmState.UPGRADE_PRE_CHECK_WARNING &&
                    isUpgradeTriggeredFromSystemUpdatePage;
            } else {
                this.showUpgradeResumeButton = preChecksCompletedWithWarning &&
                    !isUpgradeTriggeredFromSystemUpdatePage;
            }
        } else {
            this.showUpgradeResumeButton = false;
        }
    }

    private async onSuppressWarningConfirmation(): Promise<void> {
        const {
            devLoggerService,
            isSystemUpdate,
            updateStore,
            userSelectedOptions,
        } = this;

        const updatedUserSelectedOptions = isSystemUpdate ? { ...userSelectedOptions } :
            this.getUpdatedUserSelectedOptions();

        try {
            await updateStore.triggerPreChecks(updatedUserSelectedOptions, true);
            updateStore.onPreChecksTrigger();
        } catch (error) {
            devLoggerService.log(error);
        }
    }

    /**
     * Return the updated user selected options.
     * In case of SEG Update, when resuming the upgrade from the right rail card,
     * we need to send only the current node id to the backend. Since, we are also maintaining a
     * list of seg ids for which we keep a track of the current operation status, we emit an event
     * to update that as well.
     */
    private getUpdatedUserSelectedOptions(): IUserSelectedOptions {
        const {
            node,
            selectedSegs,
            selectedSegUpgradeImages,
            userSelectedOptions,
        } = this;

        const selectedSeg = selectedSegs?.filter(
            (selectedSegItem: ITSEGroup) => selectedSegItem.id === node.id,
        );

        const updatedUpgradeConfig = UpdateService.getSegUpgradeConfig(
            selectedSeg,
            selectedSegUpgradeImages,
        );

        const updatedUserSelectedOptions = {
            ...userSelectedOptions,
            upgradeConfig: { ...updatedUpgradeConfig },
        };

        this.onSelectedSegIdUpdate.emit(node.id);

        this.updateStore.setUserSelectedOptionsState(updatedUserSelectedOptions);

        return updatedUserSelectedOptions;
    }

    /**
     * Return the current node version.
     */
    private getCurrentNodeVersion(): string {
        if (this.isPreCheckFlow) {
            return this.node.getImageRefVersion() ?? this.rollbackVersion;
        }

        if (this.isDryRunFlow) {
            return this.node.getDryrunImageRefVersion();
        }

        if (this.node.stateGroup === UpgradeStateGroup.GROUP_ERROR) {
            return this.node.getUpgradeFailureImageRefVersion();
        }

        return this.node.getVersion();
    }

    /**
     * Return the current node operation.
     */
    private getNodeOperation(): string {
        if (this.isDryRunFlow) {
            return this.node.getDryrunOperationType();
        }

        return this.node.upgradeOps;
    }

    /**
     * Return the operation start time.
     */
    private getOperationStartTime(): string {
        if (this.isDryRunFlow) {
            return this.node.dryrunOperationStartTime;
        }

        return this.node.upgradeStartTime;
    }
}
